import React, { useState, useEffect, useMemo } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import LockIcon from '@mui/icons-material/Lock';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createNotificationRule,
  updateNotificationRule,
  previewNotificationRule,
} from '../../services/notificationService';

const CLIENT_CONDITIONS = [
  { value: 'no_payment', label: 'לא שילם תוך', paramType: 'months' },
  { value: 'no_charge', label: 'לא חויב תוך', paramType: 'months' },
  { value: 'no_transaction', label: 'ללא תנועות תוך', paramType: 'months' },
  { value: 'debt_threshold', label: 'חוב מעל', paramType: 'amount' },
];

const SUPPLIER_CONDITIONS = [
  { value: 'supplier_no_payment', label: 'לא שולם תוך', paramType: 'months' },
  { value: 'supplier_no_purchase', label: 'ללא רכישות תוך', paramType: 'months' },
  { value: 'supplier_balance_above', label: 'יתרה מעל', paramType: 'amount' },
];

const PRODUCT_CONDITIONS = [
  { value: 'product_low_stock', label: 'מלאי מתחת ל', paramType: 'amount' },
  { value: 'product_out_of_stock', label: 'אזל מהמלאי', paramType: 'none' },
];

const CHANNELS = [
  { value: 'in_app', label: 'באפליקציה', enabled: true },
  { value: 'email', label: 'אימייל', enabled: false },
  { value: 'sms', label: 'SMS', enabled: false },
  { value: 'whatsapp', label: 'WhatsApp', enabled: false },
];

const NotificationRuleDialog = ({ open, onClose, onSaved, editingRule }) => {
  const isEditing = Boolean(editingRule);

  const [entityType, setEntityType] = useState('client');
  const [ruleType, setRuleType] = useState('');
  const [thresholdMonths, setThresholdMonths] = useState('');
  const [thresholdAmount, setThresholdAmount] = useState('');
  const [requireDebt, setRequireDebt] = useState(true);
  const [debtThreshold, setDebtThreshold] = useState('0');
  const [channel, setChannel] = useState('in_app');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  // Reset form when dialog opens or editing rule changes
  useEffect(() => {
    if (open) {
      if (editingRule) {
        setEntityType(editingRule.EntityType || 'client');
        setRuleType(editingRule.RuleType || '');
        setThresholdMonths(editingRule.ThresholdMonths?.toString() || '');
        setThresholdAmount(editingRule.ThresholdAmount?.toString() || '');
        setRequireDebt(editingRule.RequireDebt ?? true);
        setDebtThreshold(editingRule.DebtThreshold?.toString() || '0');
        setChannel(editingRule.NotifyChannel || 'in_app');
        setName(editingRule.Name || '');
      } else {
        setEntityType('client');
        setRuleType('');
        setThresholdMonths('');
        setThresholdAmount('');
        setRequireDebt(true);
        setDebtThreshold('0');
        setChannel('in_app');
        setName('');
      }
      setError('');
    }
  }, [open, editingRule]);

  const conditions = entityType === 'client' ? CLIENT_CONDITIONS : entityType === 'product' ? PRODUCT_CONDITIONS : SUPPLIER_CONDITIONS;
  const selectedCondition = conditions.find(c => c.value === ruleType);
  const paramType = selectedCondition?.paramType || 'months';

  // Auto-generate name from selections
  const autoName = useMemo(() => {
    if (!selectedCondition) return '';
    const entityLabel = entityType === 'client' ? 'לקוח' : entityType === 'product' ? 'מוצר' : 'ספק';
    const condLabel = selectedCondition.label;
    if (paramType === 'none') {
      return `${entityLabel} ${condLabel}`;
    }
    if (paramType === 'months' && thresholdMonths) {
      return `${entityLabel} ${condLabel} ${thresholdMonths} חודשים`;
    }
    if (paramType === 'amount' && thresholdAmount) {
      const unit = entityType === 'product' ? '' : '₪';
      return `${entityLabel} ${condLabel} ${unit}${thresholdAmount}`;
    }
    return `${entityLabel} ${condLabel}`;
  }, [entityType, selectedCondition, paramType, thresholdMonths, thresholdAmount]);

  // Preview data
  const previewPayload = useMemo(() => {
    if (!ruleType) return null;
    return {
      EntityType: entityType,
      RuleType: ruleType,
      ThresholdMonths: paramType === 'months' && thresholdMonths ? parseInt(thresholdMonths) : null,
      ThresholdAmount: paramType === 'amount' && thresholdAmount ? parseFloat(thresholdAmount) : null,
      DebtThreshold: parseFloat(debtThreshold) || 0,
      RequireDebt: entityType === 'client' && ruleType !== 'debt_threshold' ? requireDebt : false,
    };
  }, [entityType, ruleType, paramType, thresholdMonths, thresholdAmount, requireDebt, debtThreshold]);

  const { data: previewData, isFetching: previewLoading } = useQuery({
    queryKey: ['notification-rule-preview', previewPayload],
    queryFn: () => previewNotificationRule(previewPayload),
    enabled: Boolean(previewPayload) && open && Boolean(
      paramType === 'none' || (paramType === 'months' && thresholdMonths) || (paramType === 'amount' && thresholdAmount)
    ),
    staleTime: 15000,
  });

  const createMutation = useMutation({
    mutationFn: createNotificationRule,
    onSuccess: () => onSaved(),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה ביצירת כלל'),
  });

  const updateMutation = useMutation({
    mutationFn: updateNotificationRule,
    onSuccess: () => onSaved(),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה בעדכון כלל'),
  });

  const handleEntityChange = (_, newVal) => {
    if (newVal) {
      setEntityType(newVal);
      setRuleType('');
      setThresholdMonths('');
      setThresholdAmount('');
    }
  };

  const handleSave = () => {
    setError('');

    if (!ruleType) {
      setError('יש לבחור תנאי');
      return;
    }

    if (paramType === 'months' && (!thresholdMonths || parseInt(thresholdMonths) < 1)) {
      setError('יש להזין מספר חודשים תקין');
      return;
    }

    if (paramType === 'amount' && (!thresholdAmount || parseFloat(thresholdAmount) < 0)) {
      setError('יש להזין סכום תקין');
      return;
    }

    // 'none' paramType (e.g. product_out_of_stock) needs no validation

    const payload = {
      Name: name || autoName,
      EntityType: entityType,
      RuleType: ruleType,
      ThresholdMonths: paramType === 'months' ? parseInt(thresholdMonths) : null,
      ThresholdAmount: paramType === 'amount' ? parseFloat(thresholdAmount) : null,
      DebtThreshold: parseFloat(debtThreshold) || 0,
      RequireDebt: entityType === 'client' && ruleType !== 'debt_threshold' ? requireDebt : false,
      NotifyChannel: channel,
      IsActive: true,
    };

    if (isEditing) {
      updateMutation.mutate({ ...payload, NotificationRuleID: editingRule.NotificationRuleID });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <MobileDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {isEditing ? 'עריכת כלל התראה' : 'כלל התראה חדש'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Step 1: Entity Type */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              סוג ישות
            </Typography>
            <ToggleButtonGroup
              value={entityType}
              exclusive
              onChange={handleEntityChange}
              size="small"
              fullWidth
            >
              <ToggleButton value="client" sx={{ textTransform: 'none', gap: 0.5, direction: 'ltr' }}>
                <BusinessIcon fontSize="small" />
                לקוח
              </ToggleButton>
              <ToggleButton value="supplier" sx={{ textTransform: 'none', gap: 0.5, direction: 'ltr' }}>
                <LocalShippingIcon fontSize="small" />
                ספק
              </ToggleButton>
              <ToggleButton value="product" sx={{ textTransform: 'none', gap: 0.5, direction: 'ltr' }}>
                <InventoryIcon fontSize="small" />
                מוצר
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Step 2: Condition */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              תנאי
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>בחר תנאי</InputLabel>
              <Select
                value={ruleType}
                onChange={(e) => setRuleType(e.target.value)}
                label="בחר תנאי"
              >
                {conditions.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Step 3: Parameter */}
          {ruleType && paramType !== 'none' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {paramType === 'months' ? 'תקופה' : 'כמות'}
              </Typography>
              {paramType === 'months' ? (
                <TextField
                  size="small"
                  type="number"
                  value={thresholdMonths}
                  onChange={(e) => setThresholdMonths(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onWheel={(e) => e.target.blur()}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">חודשים</InputAdornment>,
                    inputProps: { min: 1, max: 24 },
                    sx: { direction: 'ltr' },
                  }}
                  fullWidth
                  placeholder="2"
                />
              ) : (
                <TextField
                  size="small"
                  type="number"
                  value={thresholdAmount}
                  onChange={(e) => setThresholdAmount(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onWheel={(e) => e.target.blur()}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{entityType === 'product' ? 'יחידות' : '₪'}</InputAdornment>,
                    inputProps: { min: 0 },
                    sx: { direction: 'ltr' },
                  }}
                  fullWidth
                  placeholder={entityType === 'product' ? '5' : '0'}
                />
              )}
            </Box>
          )}

          {/* Step 4: Debt compound filter (client only, not for debt_threshold) */}
          {entityType === 'client' && ruleType && ruleType !== 'debt_threshold' && (
            <Box sx={{ backgroundColor: 'grey.50', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={requireDebt}
                onChange={(e) => setRequireDebt(e.target.checked)}
                size="small"
                sx={{ p: 0.5 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                וגם חוב מעל
              </Typography>
              {requireDebt && (
                <TextField
                  size="small"
                  type="number"
                  value={debtThreshold}
                  onChange={(e) => setDebtThreshold(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onWheel={(e) => e.target.blur()}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₪</InputAdornment>,
                    inputProps: { min: 0 },
                    sx: { direction: 'ltr' },
                  }}
                  sx={{ width: { xs: '100%', sm: 140 } }}
                  placeholder="0"
                />
              )}
            </Box>
          )}

          {/* Step 5: Channel */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              ערוץ התראה
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {CHANNELS.map((ch) => (
                <Chip
                  key={ch.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, direction: 'ltr' }}>
                      {ch.label}
                      {!ch.enabled && (
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                          בקרוב
                        </Typography>
                      )}
                    </Box>
                  }
                  icon={!ch.enabled ? <LockIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                  variant={channel === ch.value ? 'filled' : 'outlined'}
                  color={channel === ch.value ? 'primary' : 'default'}
                  onClick={() => ch.enabled && setChannel(ch.value)}
                  disabled={!ch.enabled}
                  sx={{
                    cursor: ch.enabled ? 'pointer' : 'default',
                    opacity: ch.enabled ? 1 : 0.5,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Rule Name */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              שם הכלל
            </Typography>
            <TextField
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              placeholder={autoName || 'שם אוטומטי'}
              helperText="השאר ריק לשם אוטומטי"
            />
          </Box>

          <Divider />

          {/* Step 6: Preview */}
          {ruleType && (
            <Box sx={{ backgroundColor: 'primary.50', borderRadius: 2, p: 2, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                תצוגה מקדימה
              </Typography>
              {previewLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">בודק...</Typography>
                </Box>
              ) : previewData ? (
                <Typography variant="body2">
                  <strong>{previewData.count}</strong>{' '}
                  {entityType === 'client' ? 'לקוחות' : entityType === 'product' ? 'מוצרים' : 'ספקים'} תואמים כרגע
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  השלם את הפרמטרים כדי לראות תצוגה מקדימה
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, direction: 'ltr' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !ruleType}
          sx={{ textTransform: 'none' }}
        >
          {isSaving ? <CircularProgress size={20} /> : isEditing ? 'עדכן' : 'צור כלל'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default NotificationRuleDialog;
